/**
 * 工作流相关的 API 路由
 */

import { Router } from 'express';
import { WorkflowManager } from '../../core/workflow-manager.js';
import { ApiError, ApiErrorCode, WorkflowQueryParams } from '../types.js';
import { logger } from '../../utils/logger.js';

const router = Router();

// 获取所有工作流
router.get('/', async (req, res, next) => {
  try {
    const query = req.query as WorkflowQueryParams;
    const workflowManager = WorkflowManager.getInstance();
    
    let workflows = await workflowManager.listWorkflows();
    
    // 应用过滤
    if (query.status) {
      workflows = workflows.filter(w => w.status === query.status);
    }
    if (query.type) {
      workflows = workflows.filter(w => w.type === query.type);
    }
    if (query.search) {
      const search = query.search.toLowerCase();
      workflows = workflows.filter(w => 
        w.name.toLowerCase().includes(search) ||
        w.description?.toLowerCase().includes(search)
      );
    }
    
    // 分页
    const page = parseInt(query.page as any) || 1;
    const limit = parseInt(query.limit as any) || 20;
    const start = (page - 1) * limit;
    const end = start + limit;
    const total = workflows.length;
    const paginatedWorkflows = workflows.slice(start, end);
    
    res.paginate(paginatedWorkflows, {
      page,
      limit,
      total,
      totalPages: Math.ceil(total / limit),
      hasNext: end < total,
      hasPrev: page > 1,
    });
  } catch (error) {
    next(error);
  }
});

// 获取单个工作流
router.get('/:id', async (req, res, next) => {
  try {
    const { id } = req.params;
    const workflowManager = WorkflowManager.getInstance();
    const workflow = await workflowManager.getWorkflow(id);
    
    if (!workflow) {
      throw new ApiError(
        ApiErrorCode.WORKFLOW_NOT_FOUND,
        `Workflow with id ${id} not found`,
        404
      );
    }
    
    res.success(workflow);
  } catch (error) {
    next(error);
  }
});

// 创建工作流
router.post('/', async (req, res, next) => {
  try {
    const workflowData = req.body;
    
    // 验证必填字段
    if (!workflowData.name || !workflowData.type) {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Name and type are required',
        400
      );
    }
    
    const workflowManager = WorkflowManager.getInstance();
    const workflow = await workflowManager.createWorkflow(workflowData);
    
    logger.info(`Workflow created: ${workflow.id}`);
    res.success(workflow, { created: true });
  } catch (error) {
    next(error);
  }
});

// 启动工作流
router.post('/:id/start', async (req, res, next) => {
  try {
    const { id } = req.params;
    const { context } = req.body;
    
    const workflowManager = WorkflowManager.getInstance();
    const workflow = await workflowManager.getWorkflow(id);
    
    if (!workflow) {
      throw new ApiError(
        ApiErrorCode.WORKFLOW_NOT_FOUND,
        `Workflow with id ${id} not found`,
        404
      );
    }
    
    if (workflow.status === 'running') {
      throw new ApiError(
        ApiErrorCode.WORKFLOW_ALREADY_RUNNING,
        'Workflow is already running',
        400
      );
    }
    
    const result = await workflowManager.startWorkflow(id, context);
    logger.info(`Workflow started: ${id}`);
    
    res.success(result);
  } catch (error) {
    next(error);
  }
});

// 停止工作流
router.post('/:id/stop', async (req, res, next) => {
  try {
    const { id } = req.params;
    
    const workflowManager = WorkflowManager.getInstance();
    const workflow = await workflowManager.getWorkflow(id);
    
    if (!workflow) {
      throw new ApiError(
        ApiErrorCode.WORKFLOW_NOT_FOUND,
        `Workflow with id ${id} not found`,
        404
      );
    }
    
    if (workflow.status !== 'running') {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Workflow is not running',
        400
      );
    }
    
    await workflowManager.stopWorkflow(id);
    logger.info(`Workflow stopped: ${id}`);
    
    res.success({ stopped: true, id });
  } catch (error) {
    next(error);
  }
});

// 获取工作流历史
router.get('/:id/history', async (req, res, next) => {
  try {
    const { id } = req.params;
    const workflowManager = WorkflowManager.getInstance();
    
    const history = await workflowManager.getWorkflowHistory(id);
    
    if (!history || history.length === 0) {
      throw new ApiError(
        ApiErrorCode.NOT_FOUND,
        'No history found for this workflow',
        404
      );
    }
    
    res.success(history);
  } catch (error) {
    next(error);
  }
});

// 获取工作流模板
router.get('/templates/list', async (req, res, next) => {
  try {
    const workflowManager = WorkflowManager.getInstance();
    const templates = await workflowManager.getTemplates();
    
    res.success(templates);
  } catch (error) {
    next(error);
  }
});

// 从模板创建工作流
router.post('/templates/:templateId/create', async (req, res, next) => {
  try {
    const { templateId } = req.params;
    const { name, description, context } = req.body;
    
    if (!name) {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Name is required',
        400
      );
    }
    
    const workflowManager = WorkflowManager.getInstance();
    const workflow = await workflowManager.createFromTemplate(templateId, {
      name,
      description,
      context,
    });
    
    logger.info(`Workflow created from template: ${workflow.id}`);
    res.success(workflow, { created: true });
  } catch (error) {
    next(error);
  }
});

// 执行工作流（支持 V2 引擎）
router.post('/execute', async (req, res, next) => {
  try {
    const { workflow, inputs } = req.body;
    
    if (!workflow || !workflow.nodes || !workflow.entry) {
      throw new ApiError(
        ApiErrorCode.VALIDATION_ERROR,
        'Invalid workflow definition',
        400
      );
    }
    
    // 动态导入 WorkflowEngineV2
    const { WorkflowEngineV2 } = await import('../../core/workflow-engine-v2.js');
    const engine = new WorkflowEngineV2();
    
    // 监听执行事件并通过 SSE 发送
    const realtimeSync = await import('../../core/realtime-sync.js');
    const syncManager = realtimeSync.getRealtimeSyncManager();
    
    engine.on('node_executed', (event) => {
      syncManager.broadcastEvent({
        type: 'workflow.node_executed',
        data: event
      });
    });
    
    engine.on('workflow_completed', (event) => {
      syncManager.broadcastEvent({
        type: 'workflow.completed',
        data: event
      });
    });
    
    engine.on('workflow_failed', (event) => {
      syncManager.broadcastEvent({
        type: 'workflow.failed',
        data: event
      });
    });
    
    // 执行工作流
    const result = await engine.execute(workflow, inputs);
    
    logger.info(`Workflow executed: ${workflow.id || 'temp'}`);
    res.success(result);
  } catch (error) {
    logger.error('Failed to execute workflow:', error);
    next(error);
  }
});

export { router as workflowRouter };